import { memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import routes from 'router/routes';
import PropTypes from 'prop-types';
import EmployeeBadge from 'components/ui-kit/employee-badge';
import LabelMark from 'components/ui-kit/label-mark';
import { formatDateSpan, formatTime, spansMultipleDays } from 'helpers/datetime.helper';
import {
  getIcon,
  getColor,
  appointmentTypes,
  appointmentBusinessTypes,
} from 'constants/appointments.js';
import { useApp } from 'hooks';
import { appointmentStatusLabels } from 'helpers/enums';
import Money from 'components/ui-kit/money';
import * as S from './styles';

function AppointmentItem({
  title,
  type,
  date,
  endDate,
  isAllDay,
  address,
  unitNumber,
  employees,
  totalAmount,
  variant,
  isCompact,
  invoice,
  invoiceId,
  status,
  id,
  to,
}) {
  const { portalId } = useParams();
  const { isPreviewMode } = useApp();
  const navigate = useNavigate();
  const redirectToInvoice = () => navigate(`/${portalId}/${routes.invoices.path}/${invoiceId}`);
  const isRow = variant === 'row';
  const isCompactMode = isCompact && isRow;
  const statusLabel = appointmentStatusLabels[status];
  const path = `/${portalId}/${routes.appointments.path}/${id}`;

  const redirectTo = isPreviewMode ? '#' : (to || path);
  // Cards carry no date of their own — they sit under a day heading — so a span is the only thing that
  // has to be spelled out there: without it nothing says the crew is still on site tomorrow.
  const isSpanning = spansMultipleDays(date, endDate);

  return (
    <S.Box
      to={redirectTo}
      $isRow={isRow}
      $hasDateLine={!isRow && isSpanning}
      className={isCompactMode && 'compact'}
      type={type}
      title={title}
    >
      <S.TypeIcon isRow={isRow} isCompact={isCompactMode}>
        {getIcon(type)}
      </S.TypeIcon>
      <S.Header isCompact={isCompactMode} isRow={isRow}>
        <S.Title isRow={isRow}>{title}</S.Title>
        {isRow && (
          <>
            <LabelMark text={appointmentTypes[type]} color={getColor(type)} />
            {!!statusLabel && (
              <S.StatusMark>
                <LabelMark
                  text={statusLabel}
                  color="metallic"
                />
              </S.StatusMark>
            )}
          </>
        )}
      </S.Header>
      {!isRow && isSpanning && (
        <S.Dates>
          <S.Label>Dates:</S.Label>
          {formatDateSpan(date, endDate)}
        </S.Dates>
      )}
      <S.Address>
        {!isRow && (<S.Label>Property:</S.Label>)}
        <div>{address}</div>
        {unitNumber && (
        <div>
          Unit #:&nbsp;
          {unitNumber}
        </div>
        )}
      </S.Address>
      {!!statusLabel && (
        <S.Status isRow={isRow}>
          <S.Label>Status:</S.Label>
          {statusLabel}
        </S.Status>
      )}
      <S.Employees isRow={isRow} isCompact={isCompactMode}>
        {employees.map((item) => (
          <EmployeeBadge
            key={item.id}
            name={`${item.firstName} ${item.lastName}`}
            phoneNumber={item.phoneNumber}
            isSmall={isCompactMode}
            imageUrl={item.logoUrl}
          />
        ))}
      </S.Employees>
      {isRow && (
        <S.DateBox isCompact={isCompactMode}>
          <S.Date>
            <S.DateIcon />
            {/* A range when the appointment runs past its first day, so the row does not imply a
                one-day visit. The time line stays the start time - the row has no space for both. */}
            {formatDateSpan(date, endDate)}
          </S.Date>
          {/* An Anytime record's stored start is the anytime window's own lower bound, not a time the
              crew committed to, so printing it reads as an arrival promise. */}
          <div>{isAllDay ? 'Anytime' : formatTime(date)}</div>
        </S.DateBox>
      )}

      {invoice !== null && invoiceId !== null && type === appointmentBusinessTypes.job && (
        <S.InvoiceRedirectButton
          onClick={redirectToInvoice}
        >
          <S.InvoiceIcon />
          {isRow && 'Invoice Details'}
        </S.InvoiceRedirectButton>
      )}

      {type !== appointmentBusinessTypes.visit && (
        <S.Total isRow={isRow} isCompact={isCompactMode}>
          <S.Label isRow={isRow} isBold>Total:</S.Label>
          <Money amount={totalAmount} />
        </S.Total>
      )}
    </S.Box>
  );
}

AppointmentItem.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  address: PropTypes.string.isRequired,
  unitNumber: PropTypes.string,
  date: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  isAllDay: PropTypes.bool,
  invoice: PropTypes.string,
  invoiceId: PropTypes.string,
  isCompact: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  employees: PropTypes.arrayOf(PropTypes.object).isRequired,
  totalAmount: PropTypes.number.isRequired,
  variant: PropTypes.string,
  status: PropTypes.string,
  to: PropTypes.string,
};

AppointmentItem.defaultProps = {
  unitNumber: '',
  variant: 'row',
  date: null,
  endDate: null,
  isAllDay: false,
  invoice: null,
  invoiceId: null,
  status: null,
  to: null,
  isCompact: false,
};

export default memo(AppointmentItem);
